import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, KeyRound, UserCircle, ShieldCheck, Link2, ImageIcon } from 'lucide-react';
import type { CMSData, Agent, MediaFile, UserRole } from '../../types/cms';
import { MediaPicker } from './MediaPicker';

interface AdminUser {
  id: string;
  username: string;
  role: UserRole;
  name?: string;
  agentId?: string;
  createdAt: string;
}

interface TeamManagerProps {
  draft: CMSData;
  setDraft: React.Dispatch<React.SetStateAction<CMSData>>;
  saveDraft: (data?: CMSData) => Promise<boolean>;
  apiBase: string;
  token: string;
  currentUsername: string;
  currentUserId: string;
  showMessage: (msg: string) => void;
  onAudit: (action: string, resource?: string, resourceId?: string, details?: Record<string, any>) => Promise<void>;
  media: MediaFile[];
  fetchMedia: () => void;
  onUploadFile?: (file: File, meta?: { category?: string; tags?: string[] }) => Promise<string | undefined>;
}

const generateId = () => `agent_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const suggestUsername = (name: string, existing: string[]) => {
  const base = name
    .trim()
    .split(/\s+/)[0]
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (!base) return '';
  let candidate = base;
  let i = 1;
  while (existing.includes(candidate)) {
    candidate = `${base}${i}`;
    i++;
  }
  return candidate;
};

export const TeamManager: React.FC<TeamManagerProps> = ({
  draft,
  setDraft,
  saveDraft,
  apiBase,
  token,
  currentUsername,
  currentUserId,
  showMessage,
  onAudit,
  media,
  fetchMedia,
  onUploadFile,
}) => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [photoPickerAgentId, setPhotoPickerAgentId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [resetUser, setResetUser] = useState<AdminUser | null>(null);
  const [activateAgent, setActivateAgent] = useState<Agent | null>(null);
  const [limitModalOpen, setLimitModalOpen] = useState(false);

  const [newName, setNewName] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newWhatsapp, setNewWhatsapp] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhoto, setNewPhoto] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newRole, setNewRole] = useState<UserRole>('staff');
  const [newPassword, setNewPassword] = useState('');

  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [activatePassword, setActivatePassword] = useState('');
  const [activateRole, setActivateRole] = useState<UserRole>('staff');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setUsers(data.users);
      else showMessage(`載入帳號失敗：${data.error}`);
    } catch (err) {
      showMessage(`載入帳號失敗：${String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [apiBase, token]);

  const linkedUserByAgent = useMemo(() => {
    const map: Record<string, AdminUser> = {};
    users.forEach((u) => {
      if (u.agentId) map[u.agentId] = u;
    });
    return map;
  }, [users]);

  const unlinkedUsers = useMemo(
    () => users.filter((u) => !u.agentId || !draft.agents.some((a) => a.id === u.agentId)),
    [users, draft.agents]
  );

  const updateAgent = async (index: number, updated: Agent) => {
    const newAgents = [...draft.agents];
    newAgents[index] = updated;
    const nextDraft = { ...draft, agents: newAgents };
    setDraft(nextDraft);
    await saveDraft(nextDraft);
  };

  const removeAgent = async (index: number) => {
    const agent = draft.agents[index];
    if (!confirm(`確定刪除成員「${agent.name || '未命名'}」？對應的登入帳號也會一併刪除。`)) return;

    const linked = linkedUserByAgent[agent.id];
    if (linked) {
      if (linked.username === currentUsername) {
        showMessage('不能刪除目前登入的帳號');
        return;
      }
      try {
        const res = await fetch(`${apiBase}/api/admin/users/${linked.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!data.success) {
          showMessage(`刪除帳號失敗：${data.error}`);
          return;
        }
        await onAudit('user_delete', 'user', linked.id, { targetUsername: linked.username, viaAgentDelete: true });
      } catch (err) {
        showMessage(`刪除帳號失敗：${String(err)}`);
        return;
      }
    }

    const newAgents = draft.agents.filter((_, i) => i !== index);
    const nextDraft = { ...draft, agents: newAgents };
    setDraft(nextDraft);
    await saveDraft(nextDraft);
    await onAudit('agent_delete', 'agent', agent.id, { name: agent.name });
    fetchUsers();
    showMessage('成員已刪除');
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName || !newUsername || !newPassword) {
      showMessage('請填寫姓名、登入帳號和初始密碼');
      return;
    }

    const agentId = generateId();
    const agent: Agent = {
      id: agentId,
      name: newName,
      title: newTitle,
      whatsapp: newWhatsapp,
      email: newEmail || undefined,
      photo: newPhoto || undefined,
    };

    const nextDraft = { ...draft, agents: [...draft.agents, agent] };
    setDraft(nextDraft);
    try {
      await saveDraft(nextDraft);
    } catch (err) {
      showMessage(`儲存成員資料失敗：${String(err)}`);
      return;
    }

    try {
      const res = await fetch(`${apiBase}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          username: newUsername,
          password: newPassword,
          role: newRole,
          name: newName,
          agentId,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showMessage('成員及登入帳號已建立');
        await onAudit('user_create', 'user', data.user.id, {
          targetUsername: newUsername,
          targetRole: newRole,
          agentId,
        });
        setAddOpen(false);
        resetAddForm();
        fetchUsers();
      } else {
        showMessage(`建立登入帳號失敗：${data.error}`);
      }
    } catch (err) {
      showMessage(`建立登入帳號失敗：${String(err)}`);
    }
  };

  const resetAddForm = () => {
    setNewName('');
    setNewTitle('');
    setNewWhatsapp('');
    setNewEmail('');
    setNewPhoto('');
    setNewUsername('');
    setNewRole('staff');
    setNewPassword('');
  };

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activateAgent || !activatePassword) return;

    const username = suggestUsername(activateAgent.name, users.map((u) => u.username));
    if (!username) {
      showMessage('無法自動產生帳號，請檢查姓名');
      return;
    }

    try {
      const res = await fetch(`${apiBase}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          username,
          password: activatePassword,
          role: activateRole,
          name: activateAgent.name,
          agentId: activateAgent.id,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showMessage(`已開通登入：${username}`);
        await onAudit('user_create', 'user', data.user.id, {
          targetUsername: username,
          targetRole: activateRole,
          agentId: activateAgent.id,
        });
        setActivateAgent(null);
        setActivatePassword('');
        setActivateRole('staff');
        fetchUsers();
      } else {
        showMessage(`開通失敗：${data.error}`);
      }
    } catch (err) {
      showMessage(`開通失敗：${String(err)}`);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetUser || !resetPasswordValue) return;
    try {
      const res = await fetch(`${apiBase}/api/admin/users/${resetUser.id}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ newPassword: resetPasswordValue }),
      });
      const data = await res.json();
      if (data.success) {
        showMessage('密碼已重設');
        await onAudit('user_password_change', 'user', resetUser.id, {
          targetUsername: resetUser.username,
          adminReset: true,
        });
        setResetUser(null);
        setResetPasswordValue('');
      } else {
        showMessage(`重設失敗：${data.error}`);
      }
    } catch (err) {
      showMessage(`重設失敗：${String(err)}`);
    }
  };

  const updateUserRole = async (user: AdminUser, role: UserRole) => {
    try {
      const res = await fetch(`${apiBase}/api/admin/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role }),
      });
      const data = await res.json();
      if (data.success) {
        showMessage('權限已更新');
        await onAudit('user_update', 'user', user.id, {
          targetUsername: user.username,
          role,
        });
        fetchUsers();
      } else {
        showMessage(`更新失敗：${data.error}`);
      }
    } catch (err) {
      showMessage(`更新失敗：${String(err)}`);
    }
  };

  const linkUserToAgent = async (user: AdminUser, agent: Agent) => {
    try {
      const res = await fetch(`${apiBase}/api/admin/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ agentId: agent.id }),
      });
      const data = await res.json();
      if (data.success) {
        showMessage(`已將 ${user.username} 連結到 ${agent.name}`);
        await onAudit('user_link_agent', 'user', user.id, {
          targetUsername: user.username,
          agentId: agent.id,
        });
        fetchUsers();
      } else {
        showMessage(`連結失敗：${data.error}`);
      }
    } catch (err) {
      showMessage(`連結失敗：${String(err)}`);
    }
  };

  const deleteUnlinkedUser = async (user: AdminUser) => {
    if (!confirm(`確定刪除未連結帳號 ${user.username}？`)) return;
    if (user.username === currentUsername) {
      showMessage('不能刪除目前登入的帳號');
      return;
    }
    try {
      const res = await fetch(`${apiBase}/api/admin/users/${user.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        showMessage('帳號已刪除');
        await onAudit('user_delete', 'user', user.id, { targetUsername: user.username });
        fetchUsers();
      } else {
        showMessage(`刪除失敗：${data.error}`);
      }
    } catch (err) {
      showMessage(`刪除失敗：${String(err)}`);
    }
  };

  const openAdd = () => {
    if (users.length >= 11) {
      setLimitModalOpen(true);
      return;
    }
    setAddOpen(true);
    resetAddForm();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900">團隊帳號</h2>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 rounded bg-blue-800 px-4 py-2 text-sm font-bold text-white hover:bg-blue-900"
        >
          <Plus className="h-4 w-4" />
          新增成員
        </button>
      </div>

      {unlinkedUsers.length > 0 && (
        <div className="rounded-xl border border-gold-200 bg-gold-50 p-4">
          <h3 className="mb-2 text-sm font-bold text-gold-800">未連結或連結遺失的帳號</h3>
          <p className="mb-3 text-xs text-gold-700">
            以下帳號未綁定銷售成員，或綁定的成員已被刪除。請重新連結到現有銷售成員，或刪除不需要的帳號。
          </p>
          <div className="space-y-2">
            {unlinkedUsers.map((user) => (
              <div key={user.id} className="flex flex-wrap items-center gap-3 rounded bg-white p-3 shadow-sm">
                <span className="font-semibold">{user.username}</span>
                <span className={`rounded px-2 py-0.5 text-xs font-bold ${user.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                  {user.role === 'admin' ? 'Admin' : 'Staff'}
                </span>
                <select
                  value=""
                  onChange={(e) => {
                    const agent = draft.agents.find((a) => a.id === e.target.value);
                    if (agent) linkUserToAgent(user, agent);
                  }}
                  className="rounded border border-slate-300 px-2 py-1 text-sm"
                >
                  <option value="">連結到成員...</option>
                  {draft.agents
                    .filter((a) => !linkedUserByAgent[a.id])
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name || a.id}
                      </option>
                    ))}
                </select>
                <button
                  onClick={() => deleteUnlinkedUser(user)}
                  className="ml-auto rounded bg-red-100 p-1.5 text-red-600 hover:bg-red-200"
                  title="刪除帳號"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-4 py-3 text-left">照片</th>
              <th className="px-4 py-3 text-left">姓名</th>
              <th className="px-4 py-3 text-left">職稱</th>
              <th className="px-4 py-3 text-left">WhatsApp</th>
              <th className="px-4 py-3 text-left">電郵</th>
              <th className="px-4 py-3 text-left">Google 轉化 Label</th>
              <th className="px-4 py-3 text-left">登入帳號</th>
              <th className="px-4 py-3 text-left">權限</th>
              <th className="px-4 py-3 text-left">操作</th>
            </tr>
          </thead>
          <tbody>
            {draft.agents.map((agent, idx) => {
              const linked = linkedUserByAgent[agent.id];
              return (
                <tr key={agent.id} className="border-t border-slate-100">
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      {agent.photo ? (
                        <img src={agent.photo} alt="" className="h-8 w-8 rounded-full object-cover" />
                      ) : (
                        <UserCircle className="h-8 w-8 text-slate-300" />
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setPhotoPickerAgentId(agent.id);
                          fetchMedia();
                        }}
                        className="rounded bg-slate-100 p-1.5 text-slate-600 hover:bg-slate-200"
                        title="從媒體庫選擇"
                      >
                        <ImageIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="text"
                      value={agent.name}
                      onChange={(e) => updateAgent(idx, { ...agent, name: e.target.value })}
                      className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-gold-600 focus:outline-none"
                      placeholder="姓名"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="text"
                      value={agent.title}
                      onChange={(e) => updateAgent(idx, { ...agent, title: e.target.value })}
                      className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-gold-600 focus:outline-none"
                      placeholder="職稱"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="text"
                      value={agent.whatsapp}
                      onChange={(e) => updateAgent(idx, { ...agent, whatsapp: e.target.value })}
                      className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-gold-600 focus:outline-none"
                      placeholder="852xxxxxxxx"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="email"
                      value={agent.email || ''}
                      onChange={(e) => updateAgent(idx, { ...agent, email: e.target.value })}
                      className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-gold-600 focus:outline-none"
                      placeholder="選填"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="text"
                      value={agent.conversionLabel || ''}
                      onChange={(e) => updateAgent(idx, { ...agent, conversionLabel: e.target.value.trim() })}
                      className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs focus:border-gold-600 focus:outline-none"
                      placeholder="AW-18405949523/xxxxxx"
                      title="Google Ads 轉化 Label——點擊此同事樓盤的 WhatsApp 按鈕時計入該同事的轉化"
                    />
                  </td>
                  <td className="px-4 py-2">
                    {linked ? (
                      <span className="font-semibold text-slate-700">{linked.username}</span>
                    ) : (
                      <span className="text-xs text-slate-400">未開通</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {linked ? (
                      <select
                        value={linked.role}
                        onChange={(e) => updateUserRole(linked, e.target.value as UserRole)}
                        disabled={linked.username === currentUsername}
                        className="rounded border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-100"
                      >
                        <option value="staff">Staff</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-2">
                      {linked ? (
                        <button
                          onClick={() => setResetUser(linked)}
                          className="rounded bg-slate-200 p-1.5 text-slate-700 hover:bg-slate-300"
                          title="重設密碼"
                        >
                          <KeyRound className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => setActivateAgent(agent)}
                          className="rounded bg-green-100 p-1.5 text-green-700 hover:bg-green-200"
                          title="開通登入"
                        >
                          <ShieldCheck className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => removeAgent(idx)}
                        className="rounded bg-red-100 p-1.5 text-red-600 hover:bg-red-200"
                        title="刪除成員"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {draft.agents.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                  尚未加入任何團隊成員
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {loading && users.length === 0 && (
        <p className="text-sm text-slate-500">載入帳號資料中...</p>
      )}

      <MediaPicker
        isOpen={!!photoPickerAgentId}
        onClose={() => setPhotoPickerAgentId(null)}
        media={media}
        apiBase={apiBase}
        mode="single"
        onSelect={(urls) => {
          if (photoPickerAgentId && urls[0]) {
            const idx = draft.agents.findIndex((a) => a.id === photoPickerAgentId);
            if (idx !== -1) {
              updateAgent(idx, { ...draft.agents[idx], photo: urls[0] });
            }
          }
          setPhotoPickerAgentId(null);
        }}
        onUpload={onUploadFile}
      />

      {limitModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="mb-4 text-lg font-bold text-slate-900">團隊帳號已達上限</h3>
            <p className="mb-4 text-sm text-slate-600">
              目前團隊帳號已達上限（11 個）。如需增加成員，請聯絡我們升級方案。
            </p>
            <a
              href="https://wa.me/85251164453?text=你好，我想升級團隊帳號方案"
              target="_blank"
              rel="noopener noreferrer"
              className="mb-4 flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-3 text-sm font-bold text-white hover:bg-green-700"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp 聯絡我們
            </a>
            <div className="flex justify-end">
              <button
                onClick={() => setLimitModalOpen(false)}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
              >
                確定
              </button>
            </div>
          </div>
        </div>
      )}

      {addOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="mb-4 text-lg font-bold text-slate-900">新增成員</h3>
            <form onSubmit={handleAddSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">姓名 *</label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => {
                      setNewName(e.target.value);
                      setNewUsername(suggestUsername(e.target.value, users.map((u) => u.username)));
                    }}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-800"
                    placeholder="Peggy Leung"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">職稱</label>
                  <input
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-800"
                    placeholder="高級物業顧問"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">WhatsApp</label>
                  <input
                    type="text"
                    value={newWhatsapp}
                    onChange={(e) => setNewWhatsapp(e.target.value)}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-800"
                    placeholder="85292368966"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">電郵</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-800"
                    placeholder="選填"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">登入帳號 *</label>
                  <input
                    type="text"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-800"
                    placeholder="peggy"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">權限 *</label>
                  <select
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value as UserRole)}
                    className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-800"
                  >
                    <option value="staff">Staff（物業管理）</option>
                    <option value="admin">Admin（全權）</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-semibold text-slate-700">初始密碼 *</label>
                  <input
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-800"
                    placeholder="輸入密碼"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-semibold text-slate-700">照片 URL</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newPhoto}
                      onChange={(e) => setNewPhoto(e.target.value)}
                      className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-800"
                      placeholder="https://..."
                    />
                    <button
                      type="button"
                      onClick={() => {
                        fetchMedia();
                        setPhotoPickerAgentId('__new__');
                      }}
                      className="rounded bg-blue-800 px-3 py-2 text-sm font-bold text-white hover:bg-blue-900"
                    >
                      媒體庫
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setAddOpen(false);
                    resetAddForm();
                  }}
                  className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={!newName || !newUsername || !newPassword}
                  className="rounded-lg bg-blue-800 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-900 disabled:opacity-50"
                >
                  建立
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetUser && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="mb-4 text-lg font-bold text-slate-900">重設密碼：{resetUser.username}</h3>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">新密碼</label>
                <input
                  type="text"
                  value={resetPasswordValue}
                  onChange={(e) => setResetPasswordValue(e.target.value)}
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-800"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setResetUser(null);
                    setResetPasswordValue('');
                  }}
                  className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={!resetPasswordValue}
                  className="rounded-lg bg-blue-800 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-900 disabled:opacity-50"
                >
                  重設
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activateAgent && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="mb-4 text-lg font-bold text-slate-900">開通登入：{activateAgent.name}</h3>
            <form onSubmit={handleActivate} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">系統建議帳號</label>
                <input
                  type="text"
                  value={suggestUsername(activateAgent.name, users.map((u) => u.username))}
                  disabled
                  className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500"
                />
                <p className="mt-1 text-xs text-slate-500">帳號會自動採用名字小寫，如有重複會加上數字。</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">權限</label>
                <select
                  value={activateRole}
                  onChange={(e) => setActivateRole(e.target.value as UserRole)}
                  className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-800"
                >
                  <option value="staff">Staff（物業管理）</option>
                  <option value="admin">Admin（全權）</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">初始密碼 *</label>
                <input
                  type="text"
                  value={activatePassword}
                  onChange={(e) => setActivatePassword(e.target.value)}
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-800"
                  placeholder="輸入密碼"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setActivateAgent(null);
                    setActivatePassword('');
                    setActivateRole('staff');
                  }}
                  className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={!activatePassword}
                  className="rounded-lg bg-blue-800 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-900 disabled:opacity-50"
                >
                  開通
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <MediaPicker
        isOpen={photoPickerAgentId === '__new__'}
        onClose={() => setPhotoPickerAgentId(null)}
        media={media}
        apiBase={apiBase}
        mode="single"
        onSelect={(urls) => {
          if (urls[0]) setNewPhoto(urls[0]);
          setPhotoPickerAgentId(null);
        }}
        onUpload={onUploadFile}
      />
    </div>
  );
};

export default TeamManager;
